import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, type Breakpoint, IconButton, Tooltip } from "@mui/material";
import { type CSSProperties, type Dispatch, type FC, type SetStateAction, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AddDayOffDialog from "../../../../components/AddDayOffDialog/AddDayOffDialog";
import { ExternalTimeOffIndicator } from "../../../../components/ExternalTimeOffIndicator/ExternalTimeOffIndicator";
import { DeletePTODialog } from "../../../../components/StaffTable/DeletePTODialog";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid, type SystemGridColDef } from "../../../../components/UIToolkit/Tables/SystemDataGrid";
import { profileTimeOffTableSortingKey } from "../../../../services/LocalStorageService";
import { formatHoursMinutes } from "../../../../utils/dateUtils";
import type { DayOff, Nurse } from "../../../../utils/models";
import type { MultiSortOption, SortingState } from "../../../../utils/multiColumnSorting";
import { useDaysOffAndPtoState } from "./useDaysOffAndPtoState";

type Props = {
	nurse: Nurse | undefined;
	setNurseData: Dispatch<SetStateAction<Nurse>>;
	onDeletePtoConfirmed: (dayOff: DayOff, restoreRecurringShifts: boolean, restoreTrimmedShifts: boolean) => Promise<void>;
	isLoading: boolean;
};

const GRID_MAX_HEIGHT: Partial<Record<Breakpoint, CSSProperties["maxHeight"]>> = {
	xs: "300px",
	xl: "500px",
};

const reasonsCompare = (a: DayOff, b: DayOff): 1 | 0 | -1 => {
	const aVal = a.reasons.join(", ");
	const bVal = b.reasons.join(", ");
	if (aVal === bVal) return 0;
	return aVal < bVal ? -1 : 1;
};

const notesCompare = (a: DayOff, b: DayOff): 1 | 0 | -1 => {
	const aVal = String(a.notes ?? "");
	const bVal = String(b.notes ?? "");
	if (aVal === bVal) return 0;
	return aVal < bVal ? -1 : 1;
};

export const DaysOffAndPTO: FC<Props> = ({ nurse, setNurseData, onDeletePtoConfirmed, isLoading }) => {
	const { t } = useTranslation();
	const {
		addPtoOpen,
		toggleAddPtoOpen,
		closeAddPtoOpen,
		handleSaveNewDayOff,
		daysOff,
		dayOffToDelete,
		handleDeleteClick,
		handleDeleteCancel,
		handleDeleteConfirm,
		dayOffToEdit,
		handleEditClick,
		handleEditSave,
		formatPtoDateRange,
		getRequestedHours,
	} = useDaysOffAndPtoState({ nurse, setNurseData, onDeletePtoConfirmed });

	const columns = useMemo<SystemGridColDef<DayOff>[]>(
		() => [
			{
				field: "dateFrom",
				headerName: t("date"),
				flex: 1,
				minWidth: 180,
				renderCell: ({ row }) => formatPtoDateRange(row),
			},
			{
				field: "reasons",
				headerName: t("type"),
				flex: 1,
				minWidth: 140,
				renderCell: ({ row }) => (row.reasons.length > 0 ? row.reasons.join(", ") : "-"),
			},
			{
				field: "notes",
				headerName: t("reason"),
				flex: 1,
				minWidth: 250,
				renderCell: ({ row }) => (
					<Tooltip title={row.notes ?? "-"}>
						<Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.notes ?? "-"}</Box>
					</Tooltip>
				),
			},
			{
				field: "hours",
				headerName: t("missed.hours.per.day"),
				description: t("missed.hours.per.day.tooltip"),
				headerTooltip: t("missed.hours.per.day.tooltip"),
				flex: 1,
				minWidth: 120,
				sortable: false,
				renderCell: ({ row }) => formatHoursMinutes(row.hours),
			},
			{
				// Not a DayOff field: hours is stored per day, so the total is derived from the entry's staffing days.
				field: "requestedHours",
				headerName: t("requested.hours"),
				description: t("requested.hours.tooltip"),
				headerTooltip: t("requested.hours.tooltip"),
				flex: 1,
				minWidth: 140,
				sortable: false,
				renderCell: ({ row }) => formatHoursMinutes(getRequestedHours(row)),
			},
			{
				field: "externalTimeOff",
				headerName: t("source"),
				flex: 1,
				minWidth: 80,
				sortable: false,
				renderCell: ({ row }) => <ExternalTimeOffIndicator externalTimeOff={row.externalTimeOff} />,
			},
			{
				field: "actions",
				headerName: t("actions"),
				flex: 1,
				minWidth: 120,
				sortable: false,
				renderCell: ({ row }) => (
					<>
						<IconButton onClick={() => handleEditClick(row)} disabled={isLoading} data-testid={`pto-edit-${row.id}`}>
							<EditIcon style={{ fontSize: "16px" }} />
						</IconButton>
						<IconButton onClick={() => handleDeleteClick(row)} disabled={isLoading} data-testid={`pto-delete-${row.id}`}>
							<DeleteIcon style={{ fontSize: "16px" }} />
						</IconButton>
					</>
				),
			},
		],
		[t, formatPtoDateRange, getRequestedHours, handleEditClick, handleDeleteClick, isLoading],
	);

	const sortingOptions = useMemo<MultiSortOption<DayOff>[]>(
		() => [
			{ label: t("date"), sortColumns: ["dateFrom"] },
			{ label: t("type"), sortColumns: ["reasons"], customSortFunction: reasonsCompare },
			{ label: t("reason"), sortColumns: ["notes"], customSortFunction: notesCompare },
		],
		[t],
	);

	const defaultSortState = useMemo<SortingState<DayOff>[]>(() => [{ sortColumns: ["dateFrom"], sortOrder: "desc" }], []);

	const addButton = (
		<SystemButton
			data-testid="create-new-pto-button"
			leadingIcon={<AddCircleOutlineIcon style={{ fontSize: "18px" }} />}
			buttonVariant="borderless"
			onClick={toggleAddPtoOpen}
		>
			{t("create.new.pto")}
		</SystemButton>
	);

	return (
		<Box sx={{ width: "100%" }}>
			<Box sx={{ width: "100%", height: GRID_MAX_HEIGHT, display: "flex", flexDirection: "column" }}>
				<SystemDataGrid<DayOff>
					rows={daysOff}
					columns={columns}
					getRowId={(row) => row.id}
					sortingOptions={sortingOptions}
					defaultSortState={defaultSortState}
					localStorageKey={profileTimeOffTableSortingKey}
					trailingToolbarNodes={addButton}
					toolbarSxOverride={{ justifyContent: "flex-end" }}
					dataTestId={"profile-time-off"}
				/>
			</Box>
			{addPtoOpen && <AddDayOffDialog open={addPtoOpen} onClose={closeAddPtoOpen} onSave={handleSaveNewDayOff} nurseId={nurse?.id ?? 0} />}
			{dayOffToEdit && (
				<AddDayOffDialog open={!!dayOffToEdit} onClose={() => handleEditClick(null)} onSave={handleEditSave} nurseId={nurse?.id ?? 0} dayOff={dayOffToEdit} />
			)}
			<DeletePTODialog
				dayOff={dayOffToDelete}
				nurseId={nurse?.id ?? null}
				isLoading={isLoading}
				onConfirm={handleDeleteConfirm}
				onCancel={handleDeleteCancel}
			/>
		</Box>
	);
};
